import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

const TICK_MS = 20000; // flush accrued foreground time every 20s
const MAX_BEAT = 120; // server-side cap; guards against clock jumps / sleep

const newId = () =>
    (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Foreground-only time-on-screen tracker for the inmate app.
 * A "session" is one continuous foreground period (a new session_id starts each time
 * the app returns to the foreground). Time is only accrued while the tab is visible,
 * so a backgrounded/locked tablet does not inflate engagement. Sends ONLY durations.
 */
export function useEngagementTracker(enabled) {
    const sessionRef = useRef(null);
    const lastBeatRef = useRef(Date.now());

    useEffect(() => {
        if (!enabled) return undefined;

        const startSession = () => {
            sessionRef.current = newId();
            lastBeatRef.current = Date.now();
        };

        const flush = () => {
            if (document.visibilityState !== "visible" || !sessionRef.current) return;
            const now = Date.now();
            const secs = Math.round((now - lastBeatRef.current) / 1000);
            lastBeatRef.current = now;
            if (secs < 1) return;
            api.post("/inmate/engagement/ping", {
                session_id: sessionRef.current,
                seconds: Math.min(secs, MAX_BEAT),
            }).catch(() => {});
        };

        const onVisibility = () => {
            if (document.visibilityState === "hidden") flush();
            else startSession();
        };

        if (document.visibilityState === "visible") startSession();
        const interval = setInterval(flush, TICK_MS);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pagehide", flush);

        return () => {
            flush();
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pagehide", flush);
        };
    }, [enabled]);
}
